import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse, WportHttpError, type EnterpriseCompanyBlockReplaceResultDto } from '@wport/core';
import { CliError, ExitCode, InvalidArgumentError } from '../../../lib/errors';
import { enterpriseGet, enterprisePut } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { readJsonObject, type ReadJsonInputOptions } from '../../../lib/io-helpers';
import { printJson, printTable, printWarn } from '../../../lib/output';
import {
	assertValidSubmittedItems,
	enforceDeletionConfirmation,
	previewDeletions,
	summarizeItemsDiff,
	getEnterpriseErrorCode,
	type ExistingBlockItem,
} from './block-replace-shared';

/**
 * `wport enterprise company replace <section>`（spec §3.2.3）：四區塊整段取代
 * （pain-points／awards／milestones／qa-items）。動詞固定 `replace`——未列出的既有項目
 * 一律刪除，不是 add/update。
 */
export const REPLACE_SECTIONS = ['pain-points', 'awards', 'milestones', 'qa-items'] as const;
export type ReplaceSection = (typeof REPLACE_SECTIONS)[number];

interface SectionConfig {
	/** `PUT /company/<...>` 相對 path。 */
	path: string;
	/** GET /company 回傳陣列欄位名，同時也是 block_revisions 的 key（兩者恆同名）。 */
	dataKey: string;
	/** 人類可讀摘要（刪除預覽 / table 輸出共用），各區塊的「標題型」欄位不同。 */
	summarize: (item: ExistingBlockItem) => string;
}

const SECTION_CONFIG: Record<ReplaceSection, SectionConfig> = {
	'pain-points': {
		path: '/company/pain-points',
		dataKey: 'pain_points',
		summarize: (i) => String(i.title ?? ''),
	},
	awards: {
		path: '/company/awards',
		dataKey: 'awards',
		summarize: (i) => String(i.name ?? ''),
	},
	milestones: {
		path: '/company/milestones',
		dataKey: 'milestones',
		summarize: (i) => String(i.title ?? ''),
	},
	'qa-items': {
		path: '/company/qa-items',
		dataKey: 'qa_items',
		summarize: (i) => String(i.question ?? ''),
	},
};

function isReplaceSection(v: string): v is ReplaceSection {
	return (REPLACE_SECTIONS as readonly string[]).includes(v);
}

/**
 * fresh-eyes F-1：改用 codegen 型別 `EnterpriseCompanyBlockReplaceResultDto`（`components['schemas']
 * .EnterpriseCompanyBlockReplaceResult`）取代原本手寫的 `BlockReplaceResult`，對齊 spec §3.3／
 * `personal-types.ts:53-56` 既有模式。`revision` 直接沿用 codegen 型別；`items` 覆寫成
 * `ExistingBlockItem[]`——上游 OpenAPI schema 對 `items` 的元素只標了 `{ type: 'object' }`
 * （見 `enterprise-schema.d.ts` 的 `Record<string, never>[]`，未進一步建模成個別 `*ResponseItem`
 * 聯集），沿用 codegen 原樣會讓 `.enc_id`／`config.summarize()` 完全存取不到；覆寫單一欄位而非整個
 * 手寫，讓其餘欄位（`revision`）仍然吃 codegen 型別、契約漂移時仍能在編譯期擋下。
 */
export type BlockReplaceResult = Omit<EnterpriseCompanyBlockReplaceResultDto, 'items'> & {
	items?: ExistingBlockItem[];
};

function printReplaceResult(
	result: BlockReplaceResult,
	config: SectionConfig,
	format: 'table' | 'json',
	color: boolean
): void {
	if (format === 'json') {
		printJson(result);
		return;
	}
	printTable(
		result.items ?? [],
		[
			{ header: 'enc_id', value: (r: ExistingBlockItem) => String(r.enc_id ?? '') },
			{ header: 'summary', value: (r: ExistingBlockItem) => config.summarize(r) },
		],
		color
	);
	// fresh-eyes F-4（NIT）：table 模式刻意印 revision，spec §3.2.1「revision 只在 JSON 顯示」是
	// 針對 `company view`（避免被當成人類可讀版本號），對 `replace` 本身沒有禁止——replace 之後
	// 使用者常常要立刻拿這個 opaque token 做下一次寫入的 If-Match，table 模式也印出來省一次
	// `--output json`。不要因為看到 view 的規則就誤以為這裡漏改。
	process.stdout.write(`revision: ${result.revision ?? ''}\n`);
}

/**
 * `company replace` 核心（spec §3.2.3）。
 *
 * 順序（早退在前，網路呼叫在後，同 `company update` 的既有慣例）：
 * 1. section enum 本地驗證 —— 不需要任何輸入或網路即可判斷
 * 2. 讀 `--file`/stdin，驗證 `{ items: [...] }` 形狀
 * 3. `GET /company` 一次：同時取得現況項目（刪除預覽用）與目標 revision（If-Match 用）
 * 4. 用現況 vs. 送出項目算刪除預覽，套用 confirm 規則（`block-replace-shared`，
 *    §3.2.3 第 2 點——非空漏一筆也算刪除，不限 `items: []`）
 * 5. `PUT` 帶 If-Match；409 一律不自動重送（§3.2.3 第 3 點）——改成重新 GET、印出
 *    server 與本地快照的差異、exit 2，要求使用者重新確認
 */
export async function runCompanyReplace(
	ctx: ResolvedContext,
	apiKey: string,
	section: string,
	source: string,
	confirm: boolean,
	idempotencyKey: string,
	options: ReadJsonInputOptions = {}
): Promise<void> {
	if (!isReplaceSection(section)) {
		throw new InvalidArgumentError(`Invalid section "${section}". Allowed: ${REPLACE_SECTIONS.join(', ')}`);
	}
	const config = SECTION_CONFIG[section];

	const input = readJsonObject(source, options);
	if (!Array.isArray(input.items)) {
		throw new InvalidArgumentError('Input must be a JSON object of the form { "items": [...] }');
	}
	// fresh-eyes F-2：光靠 `Array.isArray` 不夠——陣列裡的元素仍可能是 `null` 或其他 primitive，
	// 那類輸入必須在這裡（任何網路呼叫之前）就被擋成 exit 2，不能留到 previewDeletions 才炸。
	const items = assertValidSubmittedItems(input.items, 'items');

	const requestOpts = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey };

	const { body: getBody } = await enterpriseGet(requestOpts, '/company');
	const current = unwrapDataResponse<Record<string, unknown>>(getBody);
	const currentItems = (current[config.dataKey] as ExistingBlockItem[] | undefined) ?? [];
	const revision = (current.block_revisions as Record<string, string> | undefined)?.[config.dataKey];
	if (!revision) {
		// 防禦性檢查：Card A(spec §3.1.1) 承諾 block_revisions 一定帶五個 key。理論上不該發生，
		// 一旦發生代表後端契約漂移，不是使用者輸入錯誤——同 company/update.ts 的
		// CONTRACT_REQUIRED_GET_FIELDS 慣例，用 ServerOrNetworkError（exit 4）。
		throw new CliError(
			`Backend contract appears broken: GET /company response is missing block_revisions.${config.dataKey}`,
			ExitCode.ServerOrNetworkError
		);
	}

	const deletions = previewDeletions(currentItems, items, config.summarize);
	enforceDeletionConfirmation(section, deletions, confirm, ctx.color);

	try {
		const { body } = await enterprisePut(requestOpts, config.path, { items }, { idempotencyKey, ifMatch: revision });
		const result = unwrapDataResponse<BlockReplaceResult>(body);
		printReplaceResult(result, config, ctx.format, ctx.color);
	} catch (err) {
		if (err instanceof WportHttpError && err.status === 409) {
			const errorCode = getEnterpriseErrorCode(err);
			if (errorCode === 'idempotency_conflict') {
				throw new InvalidArgumentError(
					'This Idempotency-Key was already used for different request content; change the key (or omit --idempotency-key to let the CLI generate one).'
				);
			}
			if (errorCode !== 'profile_revision_conflict') {
				throw new InvalidArgumentError(
					`Request rejected with an unclassified 409 conflict while replacing "${section}"; the CLI will not auto-retry.`
				);
			}
			// spec §3.2.3 第 3 點：409 絕不自動重送。重新 GET 一次算出差異、印出來，
			// 然後本地 exit 2 —— 不再呼叫第二次 PUT，交還使用者重新確認。
			const { body: freshBody } = await enterpriseGet(requestOpts, '/company');
			const freshCurrent = unwrapDataResponse<Record<string, unknown>>(freshBody);
			const freshItems = (freshCurrent[config.dataKey] as ExistingBlockItem[] | undefined) ?? [];
			const diff = summarizeItemsDiff(currentItems, freshItems);
			printWarn(`Server state for "${section}" changed since your read: ${diff}.`, ctx.color);
			throw new InvalidArgumentError(
				`Refusing to auto-retry "${section}" replace after a stale revision conflict (409). ${diff}. ` +
					'Re-run `wport enterprise company view` and retry `company replace --confirm` against the current state.'
			);
		}
		throw err;
	}
}

interface ReplaceFlags {
	file?: string;
	confirm?: boolean;
	idempotencyKey?: string;
}

export function registerEnterpriseCompanyReplace(parent: Command): void {
	parent
		.command('replace <section>')
		.description(
			`Replace an entire company profile block from a JSON file. <section> is one of: ${REPLACE_SECTIONS.join('|')}. ` +
				'This is a full replacement, not an add/update: any existing item not listed in the input is hard-deleted. A `company view --output json` backup cannot be restored as-is after deletion: strip every `enc_id` first to recreate items with new IDs. Example: `jq \'{items: [.awards[] | del(.enc_id)]}\' company-backup.json > awards-restore.json`.'
		)
		.requiredOption('--file <path>', 'path to a JSON body { "items": [...] }, or "-" for stdin')
		.option('--confirm', 'confirm deletion of existing items that are not present in the input')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (section: string, flags: ReplaceFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runCompanyReplace(
				ctx,
				key,
				section,
				flags.file as string,
				flags.confirm === true,
				flags.idempotencyKey ?? randomUUID(),
				{ timeoutMs: ctx.timeoutMs }
			);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { SECTION_CONFIG, isReplaceSection };
